import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { planNameMessages } from '../music24/config/music24PlanNames';
import { useSwitchWorkspace } from './useSwitchWorkspace';
import { WorkspaceSwitchError } from './WorkspaceSwitchError';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

/**
 * design: workspaces f1 "Billing for your own workspace". A member of the active workspace opens
 * the billing of their home workspace: the switch lands on Settings > Subscription there. Nothing
 * when the home workspace is the active one (an old-model member, D8) or is not known yet.
 */
const SwitchToHomeWorkspaceComponent: React.FC = () => {
  const intl = useIntl();
  const { rows, switchTo, pendingLicenseId, failure } = useSwitchWorkspace(undefined, 'subscription');
  const home = useMemo(() => rows.find((workspace) => workspace.isHome), [rows]);
  if (home === undefined || home.isActive) {
    return null;
  }
  return (
    <Panel as="section" className="mt-4">
      <PanelHead
        title={intl.formatMessage({
          id: 'app.sml.settings.subscription.homeBillingTitle',
          defaultMessage: 'Billing for your own workspace',
        })}
        subtitle={intl.formatMessage(
          {
            id: 'app.sml.settings.subscription.homeBillingBody',
            defaultMessage:
              '{workspace} has its own plan ({plan}). Open it to change the plan, see invoices or manage the card.',
          },
          { workspace: home.name, plan: intl.formatMessage(planNameMessages[home.plan]) }
        )}
        right={
          <Button
            color="secondary"
            outline
            disabled={pendingLicenseId !== null}
            onClick={() => switchTo(home.licenseId)}
          >
            {intl.formatMessage(
              { id: 'app.sml.settings.subscription.switchHome', defaultMessage: 'Switch to {workspace}' },
              { workspace: home.name }
            )}
          </Button>
        }
      />
      {failure !== null && (
        <PanelBody>
          <WorkspaceSwitchError
            failure={failure}
            workspaceName={failure.workspaceName}
            onRetry={() => switchTo(failure.licenseId)}
          />
        </PanelBody>
      )}
    </Panel>
  );
};

export const SwitchToHomeWorkspace = TypedMemo(SwitchToHomeWorkspaceComponent);
